import React, { useEffect, useRef, useState } from "react";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { Slider } from "primereact/slider";
import { useTranslation } from "react-i18next";
import { POSModel } from "../../models/orders/order-list-model";
import {
  GetManufacrturingunitlist,
  getRawMaterialByPosId,
} from "../../services/RawMaterials/RawMaterialService";
import vendorService from "../../services/vendors/vendor.service";
import { VendorSummaryDTO } from "../../models/vendors/vendor-model";
import "../../assests/css/common/responsive-ui.css";

interface RawMaterialHistorySearchProps {
  onUnitChange: (unitIds: number[]) => void;
  onSearch?: (
    query: string,
    fromDate?: Date | null,
    toDate?: Date | null,
    vendorId?: number | null,
    rawMaterialIds?: number[] | null,
    utilizedPercentRange?: [number, number]
  ) => void;
  onReset?: () => void;
  setLoading?: (loading: boolean) => void;
}

const RawMaterialHistorySearch: React.FC<RawMaterialHistorySearchProps> = ({
  onUnitChange,
  onSearch,
  onReset,
  setLoading,
}) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [manufacturingUnits, setManufacturingUnits] = useState<POSModel[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<number[]>([]);
  const [vendors, setVendors] = useState<{ label: string; value: number }[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<number | null>(null);
  const [rawMaterials, setRawMaterials] = useState<{ label: string; value: number }[]>([]);
  const [selectedRawMaterials, setSelectedRawMaterials] = useState<number[]>([]);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [utilizedPercentRange, setUtilizedPercentRange] = useState<[number, number]>([0, 100]);

  const aliasMU = (id: number, name?: string) => {
    if (id === 3) return "Unit-1-KR";
    if (id === 4) return "Unit-2-MT";
    const up = (name ?? "").toUpperCase().replace(/\s+/g, " ").trim();
    if (/\bK\sR\sNAGAR\b/.test(up) || up.includes("KR NAGAR")) return "Unit-1-KR";
    if (up.includes("MAGADI TALUK") || up.includes("MAGADI")) return "Unit-2-MT";
    return name ?? "";
  };

  const showMessage = (label: string, message: string, severity: "success" | "warn" | "error") => {
    toast.current?.show({
      severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const getManufacturingUnits = async () => {
    try {
      if (setLoading) setLoading(true);
      const response: POSModel[] = await GetManufacrturingunitlist();
      setManufacturingUnits(response);
    } catch (error) {
      console.error("Failed to fetch manufacturing units", error);
      showMessage("Error", "Failed to fetch Manufacturing Units", "error");
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  const getVendors = async () => {
    try {
      const response: VendorSummaryDTO[] = await vendorService.getAllVendors();
      const mapped = response.map((v) => ({
        label: `${v.firmInfo} ${v.officeContactNbr ? `(${v.officeContactNbr})` : ""}`,
        value: v.vendorID,
      }));
      setVendors(mapped);
    } catch (error) {
      console.error("Failed to fetch vendors", error);
      showMessage("Error", "Failed to fetch vendor list", "error");
    }
  };

  const fetchRawMaterials = async (unitIds: number[]) => {
    try {
      setRawMaterials([]);
      if (setLoading) setLoading(true);
      const response = await getRawMaterialByPosId({ posIds: unitIds });
      const mapped = (response || []).map((r: any) => ({
        label: r.rawMaterialName,
        value: r.rawMaterialId,
      }));
      setRawMaterials(mapped);
    } catch (error) {
      console.error("Failed to fetch raw materials", error);
      showMessage("Error", "Failed to load Raw Materials for selected units", "error");
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  useEffect(() => {
    getManufacturingUnits();
    getVendors();
  }, []);

  const handleUnitsChange = (e: MultiSelectChangeEvent) => {
    const newSelectedUnits = e.value || [];
    setSelectedUnits(newSelectedUnits);
    onUnitChange(newSelectedUnits);
    if (newSelectedUnits.length > 0) fetchRawMaterials(newSelectedUnits);
  };

  const handleSearchClick = () => {
    if (selectedUnits.length === 0) {
      showMessage("Warning", "Please select at least one Manufacturing Unit", "warn");
      return;
    }

    onUnitChange(selectedUnits);
    onSearch?.(
      "",
      fromDate,
      toDate,
      selectedVendor,
      selectedRawMaterials.length > 0 ? selectedRawMaterials : null,
      utilizedPercentRange
    );
  };

  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedVendor(null);
    setSelectedRawMaterials([]);
    setSelectedUnits([]);
    setRawMaterials([]);
    setUtilizedPercentRange([0, 100]);
    onReset?.();
  };

  return (
    <div className="col-12" style={{ width: "100%", padding: "0px", marginBottom: "0.5rem" }}>
      <Toast ref={toast} />
      <div className="card border-noround p-2" style={{ minHeight: "3.5rem" }}>
        <div className="grid align-items-center">
          {/* Manufacturing Units */}
          <div className="col-12 md:col-3">
            <MultiSelect
              value={selectedUnits}
              options={manufacturingUnits.map((unit) => ({
                label: aliasMU(unit.posID, unit.posCode || unit.address2),
                value: unit.posID,
              }))}
              onChange={handleUnitsChange}
              placeholder="Select Manufacturing Units"
              display="chip"
              className="w-full"
              filter
              showClear
              maxSelectedLabels={3}
            />
          </div>

          {/* Date Range */}
          <div className="col-6 md:col-2">
            <Calendar
              value={fromDate}
              onChange={(e) => setFromDate(e.value as Date)}
              placeholder="From Date"
              showIcon
              dateFormat="dd-mm-yy"
              className="w-full"
              maxDate={toDate || new Date()}
            />
          </div>
          <div className="col-6 md:col-2">
            <Calendar
              value={toDate}
              onChange={(e) => setToDate(e.value as Date)}
              placeholder="To Date"
              showIcon
              dateFormat="dd-mm-yy"
              className="w-full"
              minDate={fromDate || undefined}
              maxDate={new Date()}
            />
          </div>

          {/* Vendor */}
          <div className="col-12 md:col-2">
            <Dropdown
              value={selectedVendor || null}
              options={vendors}
              onChange={(e: DropdownChangeEvent) => setSelectedVendor(e.value)}
              placeholder="Select Vendor"
              className="w-full"
              filter
              showClear
            />
          </div>

          {/* Raw Materials */}
          <div className="col-12 md:col-2">
          <MultiSelect
  value={selectedRawMaterials}
  options={rawMaterials}
  onChange={(e: MultiSelectChangeEvent) => {
    const newValues = e.value || [];
    setSelectedRawMaterials(newValues);
    if (newValues.length === 0) {
    }
  }}
  placeholder="Select Raw Materials"
  className="w-full"
  display="chip"
  showClear
  disabled={selectedUnits.length === 0}
  filter
  maxSelectedLabels={3}
/>

          </div>

          {/* Utilized Percentage */}
          <div className="col-12 md:col-3">
            <div className="font-semibold mb-1">
              Utilized Qty: {utilizedPercentRange[0]}% - {utilizedPercentRange[1]}%
            </div>
            <Slider
              value={utilizedPercentRange}
              onChange={(e) => setUtilizedPercentRange(e.value as [number, number])}
              range
              min={0}
              max={100}
              step={1}
              className="w-full"
            />
          </div>

          {/* Buttons (Icons Only) */}
          <div className="flex align-items-center gap-2 mt-3">
            <Button
              icon="pi pi-search"
              onClick={handleSearchClick}
              severity="success"
              className="p-button-rounded p-button-success"
              tooltip="Search"
              disabled={selectedUnits.length === 0}
            />
            <Button
              icon="pi pi-refresh"
              onClick={handleReset}
              severity="secondary"
              className="p-button-rounded p-button-secondary"
              tooltip="Reset"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RawMaterialHistorySearch;
